import React, { useState } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import { Modal, Button, Typography } from '@crisellevaldez/speculo-ui';
import { HeroSection, PendingImages } from './sections/HeroSection';
import { AboutSection, AboutContent } from './sections/AboutSection';
import { FeatureLocationSection, FeatureLocationContent } from './sections/FeatureLocationSection';
import { TestimonialsSection, TestimonialsContent } from './sections/TestimonialsSection';
import { MeetTheTeamSection, MeetTheTeamContent } from './sections/MeetTheTeamSection';
import { FooterSection, FooterContent } from './sections/FooterSection';
import { BuilderToolbar } from './components/BuilderToolbar';
import { ColorPicker } from './components/ColorPicker';
import { StageContainer } from '../components/StageContainer';
import { InfoNotice } from '../components/InfoNotice';
import { NextButton } from '../components/NextButton';
import { TeamContact } from '../components/DesignTeamContact';
import { uploadImageToStorage, deleteImageFromStorage } from './utils/imageUpload';
import { builderContentSchema } from './schemas/builderContent.schema';
import { LoadingDialog } from '../../../common/dialogs/LoadingDialog';
import { ZodError } from 'zod';

export interface HeroContent {
  backgroundImage: string;
  backgroundVideo?: string; // Optional video URL for hero background
  logo: string;
  headline: string;
  tagline: string;
  primaryCTA: string; // "BUY" button text
  secondaryCTA: string; // "SELL" button text
  stats: Array<{
    value: string;
    label: string;
  }>;
}

export interface BuilderContent {
  hero: HeroContent;
  about: AboutContent;
  featureLocation: FeatureLocationContent;
  testimonials: TestimonialsContent;
  meetTheTeam: MeetTheTeamContent;
  footer: FooterContent;
  accentColor: string; // Hex color for buttons and accents
  designUpdateEmails?: string[]; // Emails to receive website update notifications
}

interface ContentBuilderProps {
  onSave: (content: BuilderContent, isDraft?: boolean) => Promise<void>;
  initialContent?: Partial<BuilderContent>;
  organizationId: string;
  onBack?: () => void;
}

export const ContentBuilder: React.FC<ContentBuilderProps> = ({
  onSave,
  initialContent,
  organizationId,
  onBack,
}) => {
  const [heroContent, setHeroContent] = useState<HeroContent>({
    backgroundImage: initialContent?.hero?.backgroundImage || '',
    backgroundVideo: initialContent?.hero?.backgroundVideo || '',
    logo: initialContent?.hero?.logo || '',
    headline: initialContent?.hero?.headline || '[Logo]',
    tagline: initialContent?.hero?.tagline || '[Tag Line]',
    primaryCTA: initialContent?.hero?.primaryCTA || 'BUY',
    secondaryCTA: initialContent?.hero?.secondaryCTA || 'SELL',
    stats: initialContent?.hero?.stats || [
      { value: '$2M+', label: '[CAREER SALES]' },
      { value: '500+', label: '[HOMES SOLD]' },
      { value: 'Top 10%', label: 'RANK / YEARS OF EXPERIENCE' },
      { value: '10+', label: 'AWARDS' },
    ],
  });

  const [aboutContent, setAboutContent] = useState<AboutContent>({
    teamPhoto: initialContent?.about?.teamPhoto || '',
    teamVideo: initialContent?.about?.teamVideo || '',
    companyName: initialContent?.about?.companyName || '[Headline]',
    bio: initialContent?.about?.bio || '[Share Your Story]',
  });

  const [featureLocationContent, setFeatureLocationContent] = useState<FeatureLocationContent>({
    locations: initialContent?.featureLocation?.locations || [
      { image: '', title: '[Location 1]' },
      { image: '', title: '[Location 2]' },
      { image: '', title: '[Location 3]' },
      { image: '', title: '[Location 4]' },
    ],
  });

  const [testimonialsContent, setTestimonialsContent] = useState<TestimonialsContent>({
    headerTagline: initialContent?.testimonials?.headerTagline || 'TESTIMONIALS',
    sectionTitle: initialContent?.testimonials?.sectionTitle || 'What Our Clients Say',
    testimonials: initialContent?.testimonials?.testimonials || [
      {
        name: '[Client Name]',
        role: '[Client Role/Location]',
        text: '[Share their experience and feedback]',
        rating: 5,
      },
      {
        name: '[Client Name]',
        role: '[Client Role/Location]',
        text: '[Share their experience and feedback]',
        rating: 5,
      },
      {
        name: '[Client Name]',
        role: '[Client Role/Location]',
        text: '[Share their experience and feedback]',
        rating: 5,
      },
    ],
    googleReviewsLink: initialContent?.testimonials?.googleReviewsLink || '',
    useGoogleReviews: initialContent?.testimonials?.useGoogleReviews || false,
  });

  const [meetTheTeamContent, setMeetTheTeamContent] = useState<MeetTheTeamContent>({
    headerTagline: initialContent?.meetTheTeam?.headerTagline || 'MEET THE TEAM',
    sectionTitle: initialContent?.meetTheTeam?.sectionTitle || 'Our Expert Team',
    teamRosterLink: initialContent?.meetTheTeam?.teamRosterLink || '',
    useTeamRosterLink: initialContent?.meetTheTeam?.useTeamRosterLink || false,
    teamMembers: initialContent?.meetTheTeam?.teamMembers || [
      {
        headshot: '',
        name: '[Team Member Name]',
        position: '[Position/Title]',
        phone: '[Phone Number]',
        email: '[Email]',
        license: '[License Number]',
        bio: '[Team member bio - This will be used on the About Us page]',
      },
      {
        headshot: '',
        name: '[Team Member Name]',
        position: '[Position/Title]',
        phone: '[Phone Number]',
        email: '[Email]',
        license: '[License Number]',
        bio: '[Team member bio - This will be used on the About Us page]',
      },
    ],
  });

  const [footerContent, setFooterContent] = useState<FooterContent>({
    socialLinks: initialContent?.footer?.socialLinks || [
      { platform: 'Facebook', url: '[Facebook URL]' },
      { platform: 'Instagram', url: '[Instagram URL]' },
      { platform: 'LinkedIn', url: '[LinkedIn URL]' },
    ],
    contactInfo: {
      phone: initialContent?.footer?.contactInfo?.phone || '[Phone Number]',
      email: initialContent?.footer?.contactInfo?.email || '[Email Address]',
      address: initialContent?.footer?.contactInfo?.address || '[Business Address]',
    },
    navigationLinks: initialContent?.footer?.navigationLinks || [
      { label: 'Home', url: '/' },
      { label: 'About Us', url: '/about' },
      { label: 'Property Search', url: '/search' },
      { label: 'Contact', url: '/contact' },
    ],
    copyrightText:
      initialContent?.footer?.copyrightText || '© 2025 [Your Company Name]. All rights reserved.',
    brokerageLogo: initialContent?.footer?.brokerageLogo || '',
    brokerageName: initialContent?.footer?.brokerageName || '[Brokerage Name]',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [savingType, setSavingType] = useState<'draft' | 'continue' | null>(null);
  const [saveProgress, setSaveProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false); // Default to edit mode
  const [pendingImages, setPendingImages] = useState<PendingImages>({});
  const [pendingTeamMedia, setPendingTeamMedia] = useState<{
    file?: File;
    preview?: string;
    type?: 'image' | 'video';
  }>({});
  const [pendingLocationImages, setPendingLocationImages] = useState<
    Array<{ file?: File; preview?: string }>
  >([{}, {}, {}, {}]);
  const [pendingHeadshots, setPendingHeadshots] = useState<
    Array<{ file?: File; preview?: string }>
  >([{}, {}]);
  const [pendingBrokerageLogo, setPendingBrokerageLogo] = useState<{
    file?: File;
    preview?: string;
  }>({});

  const [accentColor, setAccentColor] = useState<string>(initialContent?.accentColor || '#FACC15'); // Default yellow-400
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [designUpdateEmails, setDesignUpdateEmails] = useState<string[]>(
    initialContent?.designUpdateEmails || []
  );

  // Helper function to group and format validation errors
  const groupValidationErrors = (errors: ZodError['errors']) => {
    const grouped: Record<string, string[]> = {};

    errors.forEach((err) => {
      const section = err.path[0] as string;
      const sectionName =
        section === 'hero'
          ? 'Hero Section'
          : section === 'about'
            ? 'About Section'
            : section === 'featureLocation'
              ? 'Featured Locations'
              : section === 'testimonials'
                ? 'Client Testimonials'
                : section === 'meetTheTeam'
                  ? 'Team Information'
                  : section === 'footer'
                    ? 'Footer & Contact Info'
                    : 'General';

      if (!grouped[sectionName]) {
        grouped[sectionName] = [];
      }

      // Make error message more user-friendly
      let message = err.message;

      // Remove technical path prefixes and simplify messages
      if (message.includes('placeholder')) {
        message = message.replace('Please replace the placeholder', 'Please update');
        message = message.replace('Please replace placeholder', 'Please update');
      }

      grouped[sectionName].push(message);
    });

    return grouped;
  };

  const updateHeroContent = (updates: Partial<HeroContent>) => {
    setHeroContent((prev) => ({ ...prev, ...updates }));
  };

  const updateAboutContent = (updates: Partial<AboutContent>) => {
    setAboutContent((prev) => ({ ...prev, ...updates }));
  };

  const updateFeatureLocationContent = (updates: Partial<FeatureLocationContent>) => {
    setFeatureLocationContent((prev) => ({ ...prev, ...updates }));
  };

  const updateTestimonialsContent = (updates: Partial<TestimonialsContent>) => {
    setTestimonialsContent((prev) => ({ ...prev, ...updates }));
  };

  const updateMeetTheTeamContent = (updates: Partial<MeetTheTeamContent>) => {
    setMeetTheTeamContent((prev) => ({ ...prev, ...updates }));
  };

  const updateFooterContent = (updates: Partial<FooterContent>) => {
    setFooterContent((prev) => ({ ...prev, ...updates }));
  };

  const handlePendingImagesChange = (images: PendingImages) => {
    setPendingImages(images);
  };

  const handleTeamMediaUpload = (file: File, mediaType: 'image' | 'video') => {
    const previewUrl = URL.createObjectURL(file);
    setPendingTeamMedia({ file, preview: previewUrl, type: mediaType });
  };

  const handleLocationImageUpload = (index: number, file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const updatedPendingImages = [...pendingLocationImages];
    updatedPendingImages[index] = { file, preview: previewUrl };
    setPendingLocationImages(updatedPendingImages);
  };

  const handleHeadshotUpload = (index: number, file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const updatedHeadshots = [...pendingHeadshots];
    updatedHeadshots[index] = { file, preview: previewUrl };
    setPendingHeadshots(updatedHeadshots);
  };

  const handleBrokerageLogoUpload = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setPendingBrokerageLogo({ file, preview: previewUrl });
  };

  const handleSave = async (skipValidation = false) => {
    setIsSaving(true);
    setSavingType(skipValidation ? 'draft' : 'continue');
    setSaveProgress(0);
    setValidationErrors({}); // Clear previous errors

    try {
      // Prepare content for validation (use existing URLs if no new uploads)
      // For hero: use pending preview or existing URL, considering if it's a video
      const heroBackgroundImage =
        pendingImages.backgroundMediaType === 'video'
          ? ''
          : pendingImages.backgroundPreview || heroContent.backgroundImage;
      const heroBackgroundVideo =
        pendingImages.backgroundMediaType === 'video'
          ? pendingImages.backgroundPreview
          : heroContent.backgroundVideo;

      // For about: use pending preview or existing URL, considering if it's a video
      const aboutTeamPhoto =
        pendingTeamMedia.type === 'video' ? '' : pendingTeamMedia.preview || aboutContent.teamPhoto;
      const aboutTeamVideo =
        pendingTeamMedia.type === 'video' ? pendingTeamMedia.preview : aboutContent.teamVideo;

      const contentToValidate = {
        hero: {
          ...heroContent,
          backgroundImage: heroBackgroundImage,
          backgroundVideo: heroBackgroundVideo,
          logo: pendingImages.logoPreview || heroContent.logo,
        },
        about: {
          ...aboutContent,
          teamPhoto: aboutTeamPhoto,
          teamVideo: aboutTeamVideo,
        },
        featureLocation: {
          locations: featureLocationContent.locations.map((location, index) => ({
            ...location,
            image: pendingLocationImages[index]?.preview || location.image,
          })),
        },
        testimonials: testimonialsContent,
        meetTheTeam: {
          ...meetTheTeamContent,
          teamMembers: meetTheTeamContent.teamMembers.map((member, index) => ({
            ...member,
            headshot: pendingHeadshots[index]?.preview || member.headshot,
          })),
        },
        footer: {
          ...footerContent,
          brokerageLogo: pendingBrokerageLogo.preview || footerContent.brokerageLogo,
        },
        accentColor: accentColor,
      };

      // Validate content BEFORE uploading (skip if saving as draft)
      if (!skipValidation) {
        const validation = builderContentSchema.safeParse(contentToValidate);

        if (!validation.success) {
          const grouped = groupValidationErrors(validation.error.errors);
          setValidationErrors(grouped);
          setShowValidationModal(true);
          return; // Stop here if validation fails
        }
      }

      // Validation passed, proceed with uploads
      let updatedHeroContent = { ...heroContent };
      let updatedAboutContent = { ...aboutContent };
      let updatedFeatureLocationContent = { ...featureLocationContent };

      // Count total uploads needed for progress tracking
      let totalUploads = 0;
      let completedUploads = 0;
      if (pendingImages.backgroundFile) totalUploads++;
      if (pendingImages.logoFile) totalUploads++;
      if (pendingTeamMedia.file) totalUploads++;
      pendingLocationImages.forEach((img) => img.file && totalUploads++);
      pendingHeadshots.forEach((h) => h.file && totalUploads++);
      if (pendingBrokerageLogo.file) totalUploads++;
      totalUploads++; // Add 1 for final save operation

      const updateProgress = () => {
        completedUploads++;
        setSaveProgress(Math.round((completedUploads / totalUploads) * 100));
      };

      setSaveProgress(5); // Start with 5%

      // Upload pending hero background to Firebase (delete old ones first)
      if (pendingImages.backgroundFile) {
        // Delete old background image/video if it exists
        if (heroContent.backgroundImage) {
          await deleteImageFromStorage(heroContent.backgroundImage);
        }
        if (heroContent.backgroundVideo) {
          await deleteImageFromStorage(heroContent.backgroundVideo);
        }
        const bgUrl = await uploadImageToStorage(
          pendingImages.backgroundFile,
          organizationId,
          pendingImages.backgroundMediaType === 'video' ? 'hero-video' : 'hero-background'
        );
        // Save to the correct field based on media type
        if (pendingImages.backgroundMediaType === 'video') {
          updatedHeroContent.backgroundVideo = bgUrl;
          updatedHeroContent.backgroundImage = ''; // Clear the other field
        } else {
          updatedHeroContent.backgroundImage = bgUrl;
          updatedHeroContent.backgroundVideo = ''; // Clear the other field
        }
        updateProgress();
      }

      if (pendingImages.logoFile) {
        // Delete old logo if it exists
        if (heroContent.logo) {
          await deleteImageFromStorage(heroContent.logo);
        }
        const logoUrl = await uploadImageToStorage(pendingImages.logoFile, organizationId, 'logo');
        updatedHeroContent.logo = logoUrl;
        updateProgress();
      }

      // Upload team media to Firebase (delete old one first)
      if (pendingTeamMedia.file) {
        // Delete old team photo/video if it exists
        if (aboutContent.teamPhoto) {
          await deleteImageFromStorage(aboutContent.teamPhoto);
        }
        if (aboutContent.teamVideo) {
          await deleteImageFromStorage(aboutContent.teamVideo);
        }
        const teamMediaUrl = await uploadImageToStorage(
          pendingTeamMedia.file,
          organizationId,
          pendingTeamMedia.type === 'video' ? 'team-video' : 'team-photo'
        );
        if (pendingTeamMedia.type === 'video') {
          updatedAboutContent.teamVideo = teamMediaUrl;
          updatedAboutContent.teamPhoto = '';
        } else {
          updatedAboutContent.teamPhoto = teamMediaUrl;
          updatedAboutContent.teamVideo = '';
        }
        updateProgress();
      }

      // Upload location images to Firebase (delete old ones first)
      const updatedLocations = [...updatedFeatureLocationContent.locations];
      for (let i = 0; i < pendingLocationImages.length; i++) {
        if (pendingLocationImages[i].file) {
          // Delete old location image if it exists
          if (featureLocationContent.locations[i]?.image) {
            await deleteImageFromStorage(featureLocationContent.locations[i].image);
          }
          const locationImageUrl = await uploadImageToStorage(
            pendingLocationImages[i].file!,
            organizationId,
            `location-${i}`
          );
          updatedLocations[i] = { ...updatedLocations[i], image: locationImageUrl };
          updateProgress();
        }
      }
      updatedFeatureLocationContent.locations = updatedLocations;

      // Upload team member headshots to Firebase (delete old ones first)
      let updatedMeetTheTeamContent = { ...meetTheTeamContent };
      const updatedTeamMembers = [...updatedMeetTheTeamContent.teamMembers];
      for (let i = 0; i < pendingHeadshots.length; i++) {
        if (pendingHeadshots[i].file && updatedTeamMembers[i]) {
          // Delete old headshot if it exists
          if (meetTheTeamContent.teamMembers[i]?.headshot) {
            await deleteImageFromStorage(meetTheTeamContent.teamMembers[i].headshot);
          }
          const headshotUrl = await uploadImageToStorage(
            pendingHeadshots[i].file!,
            organizationId,
            `team-headshot-${i}`
          );
          updatedTeamMembers[i] = { ...updatedTeamMembers[i], headshot: headshotUrl };
          updateProgress();
        }
      }
      updatedMeetTheTeamContent.teamMembers = updatedTeamMembers;

      // Upload brokerage logo to Firebase (delete old one first)
      let updatedFooterContent = { ...footerContent };
      if (pendingBrokerageLogo.file) {
        // Delete old brokerage logo if it exists
        if (footerContent.brokerageLogo) {
          await deleteImageFromStorage(footerContent.brokerageLogo);
        }
        const brokerageLogoUrl = await uploadImageToStorage(
          pendingBrokerageLogo.file,
          organizationId,
          'brokerage-logo'
        );
        updatedFooterContent.brokerageLogo = brokerageLogoUrl;
        updateProgress();
      }

      // Save all content (pass isDraft flag)
      await onSave(
        {
          hero: updatedHeroContent,
          about: updatedAboutContent,
          featureLocation: updatedFeatureLocationContent,
          testimonials: testimonialsContent,
          meetTheTeam: updatedMeetTheTeamContent,
          footer: updatedFooterContent,
          accentColor: accentColor,
        },
        skipValidation
      );

      // After successful save, update local state with uploaded URLs and clear pending files
      // This prevents re-uploading on subsequent saves
      setHeroContent(updatedHeroContent);
      setAboutContent(updatedAboutContent);
      setFeatureLocationContent(updatedFeatureLocationContent);
      setMeetTheTeamContent(updatedMeetTheTeamContent);
      setFooterContent(updatedFooterContent);

      // Clear all pending file states to prevent re-upload
      setPendingImages({});
      setPendingTeamMedia({});
      setPendingLocationImages([{}, {}, {}, {}]);
      setPendingHeadshots([{}, {}]);
      setPendingBrokerageLogo({});
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingType(null);
    }
  };

  return (
    <div className="min-h-100 bg-gradient-to-br from-gray-50 to-yellow-50">
      {/* Toolbar */}
      <BuilderToolbar
        showPreview={showPreview}
        onTogglePreview={() => setShowPreview(!showPreview)}
        onBack={onBack}
      />

      {/* Builder Content - Using Large StageContainer */}
      <div className="pt-8">
        <StageContainer
          title="Visual Page Builder"
          description="Click on any text to edit, drag and drop images to update."
          variant="large"
        >
          <div className="space-y-0">
            {/* Preview Notice */}
            <div className="mb-6">
              <InfoNotice>
                <div className="flex items-start gap-3">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-700">
                      We'll take your content and transform it into a stunning final website! Our
                      design team will apply your brand colors, custom fonts, professional layouts,
                      and incorporate any additional requests you've shared. Think of this as your
                      content blueprint – the final polished version will exceed your expectations!
                      ✨
                    </p>
                  </div>
                </div>
              </InfoNotice>
            </div>

            {/* Accent Color Picker */}
            <div className="mb-6">
              <ColorPicker value={accentColor} onChange={setAccentColor} />
            </div>

            {/* Hero Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <HeroSection
                content={heroContent}
                onUpdate={updateHeroContent}
                organizationId={organizationId}
                isPreview={showPreview}
                onPendingImagesChange={handlePendingImagesChange}
                accentColor={accentColor}
              />
            </div>

            {/* About Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <AboutSection
                content={
                  pendingTeamMedia.preview
                    ? pendingTeamMedia.type === 'video'
                      ? { ...aboutContent, teamVideo: pendingTeamMedia.preview, teamPhoto: '' }
                      : { ...aboutContent, teamPhoto: pendingTeamMedia.preview, teamVideo: '' }
                    : aboutContent
                }
                onUpdate={updateAboutContent}
                organizationId={organizationId}
                isPreview={showPreview}
                onTeamMediaUpload={handleTeamMediaUpload}
                pendingMediaType={pendingTeamMedia.type}
                accentColor={accentColor}
              />
            </div>

            {/* Feature Location Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <FeatureLocationSection
                content={{
                  ...featureLocationContent,
                  locations: featureLocationContent.locations.map((location, index) =>
                    pendingLocationImages[index]?.preview
                      ? { ...location, image: pendingLocationImages[index].preview! }
                      : location
                  ),
                }}
                onUpdate={updateFeatureLocationContent}
                organizationId={organizationId}
                isPreview={showPreview}
                onLocationImageUpload={handleLocationImageUpload}
                accentColor={accentColor}
              />
            </div>

            {/* Testimonials Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <TestimonialsSection
                content={testimonialsContent}
                onUpdate={updateTestimonialsContent}
                organizationId={organizationId}
                isPreview={showPreview}
                accentColor={accentColor}
              />
            </div>

            {/* Meet the Team Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <MeetTheTeamSection
                content={{
                  ...meetTheTeamContent,
                  teamMembers: meetTheTeamContent.teamMembers.map((member, index) =>
                    pendingHeadshots[index]?.preview
                      ? { ...member, headshot: pendingHeadshots[index].preview! }
                      : member
                  ),
                }}
                onUpdate={updateMeetTheTeamContent}
                organizationId={organizationId}
                isPreview={showPreview}
                onHeadshotUpload={handleHeadshotUpload}
                accentColor={accentColor}
              />
            </div>

            {/* Footer Section */}
            <div className="bg-white shadow-xl overflow-hidden">
              <FooterSection
                content={
                  pendingBrokerageLogo.preview
                    ? { ...footerContent, brokerageLogo: pendingBrokerageLogo.preview }
                    : footerContent
                }
                onUpdate={updateFooterContent}
                organizationId={organizationId}
                isPreview={showPreview}
                accentColor={accentColor}
                onBrokerageLogoUpload={handleBrokerageLogoUpload}
              />
            </div>

            {/* Save Buttons */}
            <div className="flex justify-end gap-3 pb-4">
              <NextButton
                type="button"
                onClick={() => handleSave(true)}
                disabled={isSaving}
                isLoading={savingType === 'draft'}
                variant="secondary"
                className="border-t-0"
              >
                Save as Draft
              </NextButton>
              <NextButton
                type="button"
                onClick={() => handleSave(false)}
                disabled={isSaving}
                isLoading={savingType === 'continue'}
                className="border-t-0"
              >
                Save & Continue
              </NextButton>
            </div>
          </div>
        </StageContainer>
      </div>

      {/* Loading Dialog for Saving */}
      <LoadingDialog
        isOpen={isSaving}
        message={
          savingType === 'draft'
            ? 'Saving your draft... 📝'
            : 'Uploading your website content... 🎨'
        }
        progress={saveProgress}
        showWarning={true}
        warningMessage="Please do not close or refresh this page. Leaving will reset the upload process."
      />

      {/* Validation Error Modal */}
      <Modal isOpen={showValidationModal} onClose={() => setShowValidationModal(false)} size="lg">
        <Modal.Header>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <Typography variant="h3" className="font-bold text-red-900">
              Please Complete All Required Fields
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-gray-700">
              Before we can save your website, please complete the following sections:
            </p>
            {Object.entries(validationErrors).map(([section, errors]) => (
              <div key={section} className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{section}</h4>
                <ul className="space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>{error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowValidationModal(false)}>
            Got it, I'll complete these
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
